/* eslint-disable no-empty */
import { useCallback, useEffect } from 'react';
import useBroadcastChannel from 'hooks/useBroadcastChannel';
import { OnMessageHandler } from 'broadcast-channel';

type Options = {
  callSelf: boolean;
};

export const useGlobalAction = (
  key: string,
  action: () => unknown,
  options?: Options
): (() => void) => {
  const bc = useBroadcastChannel(key);

  const runAction = useCallback(() => {
    bc.postMessage({ type: 'run' });
    if (options?.callSelf) {
      action();
    }
  }, [action, bc, options?.callSelf]);

  useEffect(() => {
    const cb = ({ type }: { type: string }) => {
      if (type === 'run') {
        if (typeof action === 'function') {
          try {
            return action();
          } catch (err: any) {}
        }
      }
    };
    bc.addEventListener('message', cb as OnMessageHandler<unknown>);

    return () => {
      bc.removeEventListener('message', cb as OnMessageHandler<unknown>);
    };
  }, [bc, action]);

  return runAction;
};

export const useAsyncGlobalAction = (
  key: string,
  action: () => Promise<void>,
  options?: Options
): (() => Promise<void>) => {
  const bc = useBroadcastChannel(key);

  const runAction = useCallback(async () => {
    bc.postMessage({ type: 'run' });
    if (options?.callSelf) {
      await action();
    }
  }, [action, bc, options?.callSelf]);

  useEffect(() => {
    const cb = async ({ type }: { type: string }) => {
      if (type === 'run') {
        if (typeof action === 'function') {
          try {
            return await action();
          } catch (err: any) {}
        }
      }
    };
    bc.addEventListener('message', cb as OnMessageHandler<unknown>);

    return () => {
      bc.removeEventListener('message', cb as OnMessageHandler<unknown>);
    };
  }, [bc, action]);

  return runAction;
};

// No-op action, reused across dispatch-only cases
const NO_OP_ACTION = () => Promise.resolve();

export const useDispatchAsyncGlobalAction = (key: string): (() => Promise<void>) => {
  return useAsyncGlobalAction(key, NO_OP_ACTION);
};
